import type { DiagnosticType } from '../domain/diagnostics';
import type { WritableEventType } from '../domain/events';
import type { PartCategory, PartItemState, PartTracking } from '../domain/parts';
import type { Role } from '../domain/roles';
import type {
  EmployeeState,
  WorkstationState,
  WorkstationType,
} from '../domain/workstations';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  apiUpload,
  queryString,
} from './client';
import type {
  AppliedInventory,
  Comparaison,
  Diagnostic,
  DiagnosticDetail,
  Employee,
  MountedPart,
  PartItem,
  PartItemHistoryEntry,
  PartReference,
  Page,
  Regularisation,
  SessionUser,
  StagingResponse,
  TimelineEvent,
  User,
  Workstation,
  WorkstationDetail,
  WorkstationOption,
} from './types';

// --- session ----------------------------------------------------------------

export const authApi = {
  login(email: string, password: string): Promise<{ user: SessionUser }> {
    return apiPost('/auth/login', { email, password });
  },
  logout(): Promise<void> {
    return apiPost('/auth/logout');
  },
  session(signal?: AbortSignal): Promise<{ user: SessionUser }> {
    return apiGet('/auth/session', signal);
  },
};

// --- utilisateurs -----------------------------------------------------------

export type UserForm = {
  email: string;
  displayName: string;
  role: Role;
  password: string;
};

export type UserPatch = {
  displayName?: string;
  role?: Role;
  isActive?: boolean;
  password?: string;
};

export const usersApi = {
  list(signal?: AbortSignal): Promise<{ users: User[] }> {
    return apiGet('/users', signal);
  },
  create(form: UserForm): Promise<{ user: User }> {
    return apiPost('/users', form);
  },
  // modification partielle : le serveur expose PATCH sur cette route
  update(id: string, patch: UserPatch): Promise<{ user: User }> {
    return apiPatch(`/users/${id}`, patch);
  },
};

// --- salariés ---------------------------------------------------------------

export type EmployeeForm = {
  lastName: string;
  firstName: string;
  department: string;
  state: EmployeeState;
  hiredOn: string;
  leftOn: string | null;
  userId: string | null;
};

export const employeesApi = {
  list(
    filters: { recherche?: string; etat?: EmployeeState } = {},
    signal?: AbortSignal,
  ): Promise<{ employees: Employee[] }> {
    return apiGet(`/employees${queryString(filters)}`, signal);
  },
  create(form: EmployeeForm): Promise<{ employee: Employee }> {
    return apiPost('/employees', form);
  },
  update(id: string, form: EmployeeForm): Promise<{ employee: Employee }> {
    return apiPut(`/employees/${id}`, form);
  },
};

// --- postes -----------------------------------------------------------------

/** Identité du poste. L'état n'en fait pas partie : il passe par le journal. */
export type WorkstationForm = {
  name: string;
  brand: string;
  model: string;
  serialNumber: string | null;
  type: WorkstationType;
  commissionedOn: string | null;
  note: string;
};

/** À la création, l'état initial devient le premier événement d'état. */
export type WorkstationCreateForm = WorkstationForm & { state: WorkstationState };

export type WorkstationQuery = {
  recherche?: string;
  etat?: WorkstationState;
  proprietaire?: string;
  page?: number;
  pageSize?: number;
};

export const workstationsApi = {
  list(query: WorkstationQuery, signal?: AbortSignal): Promise<Page<Workstation>> {
    return apiGet(`/workstations${queryString(query)}`, signal);
  },
  options(signal?: AbortSignal): Promise<{ workstations: WorkstationOption[] }> {
    return apiGet('/workstations/options', signal);
  },
  get(id: string, signal?: AbortSignal): Promise<{ workstation: WorkstationDetail }> {
    return apiGet(`/workstations/${id}`, signal);
  },
  create(form: WorkstationCreateForm): Promise<{ workstation: WorkstationDetail }> {
    return apiPost('/workstations', form);
  },
  update(id: string, form: WorkstationForm): Promise<{ workstation: WorkstationDetail }> {
    return apiPut(`/workstations/${id}`, form);
  },
  events(id: string, signal?: AbortSignal): Promise<{ events: TimelineEvent[] }> {
    return apiGet(`/workstations/${id}/events`, signal);
  },
  /**
   * Configuration matérielle du poste. Sans instant, celle d'aujourd'hui ;
   * avec, celle de la date demandée — même mécanique bornée que l'état.
   */
  parts(
    id: string,
    at?: string,
    signal?: AbortSignal,
  ): Promise<{ at: string; parts: MountedPart[] }> {
    return apiGet(`/workstations/${id}/parts${queryString({ a: at })}`, signal);
  },
  /** État du poste à une date arbitraire ; nul avant son premier état connu. */
  stateAt(
    id: string,
    at: string,
    signal?: AbortSignal,
  ): Promise<{ workstationId: string; at: string; state: WorkstationState | null }> {
    return apiGet(`/workstations/${id}/state${queryString({ a: at })}`, signal);
  },
};

// --- journal ----------------------------------------------------------------

/**
 * L'identifiant est produit par le client qui crée l'événement, jamais par la
 * base : c'est ce qui rendra un rejeu de synchronisation idempotent.
 */
export function newEventId(): string {
  return crypto.randomUUID();
}

export type EventForm = {
  id: string;
  /** Nul pour une régularisation de stock, qui ne concerne aucun poste. */
  workstationId: string | null;
  type: WritableEventType;
  occurredAt: string;
  reason: string;
  payload: Record<string, unknown>;
};

export const eventsApi = {
  append(form: EventForm): Promise<{ event: TimelineEvent; deduplicated: boolean }> {
    return apiPost('/events', form);
  },
};

// --- diagnostics ------------------------------------------------------------

export type DiagnosticQuery = {
  poste?: string;
  type?: DiagnosticType;
  dateDebut?: string;
  dateFin?: string;
  heureDebut?: string;
  heureFin?: string;
  tri?: 'recent' | 'ancien';
  page?: number;
  pageSize?: number;
};

export type ImportForm = {
  stagingId: string;
  eventId: string;
  workstationId: string;
  type: DiagnosticType;
  performedAt: string;
  reportUrl: string | null;
  reason: string;
  confirmed: {
    processeur?: string;
    carteGraphique?: string;
    memoire?: string;
    disques?: string[];
  };
  retainedLines: number[];
};

export const diagnosticsApi = {
  list(query: DiagnosticQuery, signal?: AbortSignal): Promise<Page<Diagnostic>> {
    return apiGet(`/diagnostics${queryString(query)}`, signal);
  },
  get(id: string, signal?: AbortSignal): Promise<{ diagnostic: DiagnosticDetail }> {
    return apiGet(`/diagnostics/${id}`, signal);
  },
  /**
   * Écart entre la configuration déclarée à la date du diagnostic et celle que
   * le rapport décrit. Lecture seule : rien ne corrige l'inventaire ici.
   */
  comparison(id: string, signal?: AbortSignal): Promise<{ comparaison: Comparaison }> {
    return apiGet(`/diagnostics/${id}/comparaison`, signal);
  },
  stage(file: File, workstationId?: string): Promise<StagingResponse> {
    const form = new FormData();
    form.set('file', file);
    if (workstationId !== undefined) form.set('workstationId', workstationId);
    return apiUpload('/diagnostics/stagings', form);
  },
  discardStaging(id: string): Promise<void> {
    return apiDelete(`/diagnostics/stagings/${id}`);
  },
  import(form: ImportForm): Promise<{ diagnostic: Diagnostic; inventory: AppliedInventory }> {
    return apiPost('/diagnostics', form);
  },
};

// --- inventaire de pièces -----------------------------------------------------

export type ReferenceForm = {
  designation: string;
  category: PartCategory;
  tracking: PartTracking;
  brand: string;
  model: string;
  note: string;
  declaredQuantity: number;
};

export type ItemForm = { serialNumber: string | null; state: PartItemState };

export type ReferenceQuery = {
  recherche?: string;
  categorie?: PartCategory;
  suivi?: PartTracking;
  /** « 1 » pour ne garder que les références en anomalie de stock. */
  anomalies?: string;
};

export const partsApi = {
  listReferences(
    query: ReferenceQuery = {},
    signal?: AbortSignal,
  ): Promise<{ references: PartReference[] }> {
    return apiGet(`/parts/references${queryString(query)}`, signal);
  },
  getReference(
    id: string,
    signal?: AbortSignal,
  ): Promise<{ reference: PartReference; items: PartItem[] }> {
    return apiGet(`/parts/references/${id}`, signal);
  },
  createReference(form: ReferenceForm): Promise<{ reference: PartReference }> {
    return apiPost('/parts/references', form);
  },
  updateReference(id: string, form: ReferenceForm): Promise<{ reference: PartReference }> {
    return apiPut(`/parts/references/${id}`, form);
  },
  createItems(referenceId: string, items: ItemForm[]): Promise<{ items: PartItem[] }> {
    return apiPost(`/parts/references/${referenceId}/items`, { items });
  },
  updateItem(id: string, form: ItemForm): Promise<{ item: PartItem }> {
    return apiPut(`/parts/items/${id}`, form);
  },
  itemHistory(
    id: string,
    signal?: AbortSignal,
  ): Promise<{ history: PartItemHistoryEntry[] }> {
    return apiGet(`/parts/items/${id}/history`, signal);
  },
  /** Régularisations d'une référence, annulées comprises et marquées. */
  regularisations(
    id: string,
    signal?: AbortSignal,
  ): Promise<{ regularisations: Regularisation[] }> {
    return apiGet(`/parts/references/${id}/regularisations`, signal);
  },
};

/**
 * Adresse du contenu brut d'un rapport, sur l'origine unique de
 * l'application. Elle ne porte que l'identifiant du diagnostic, et la session
 * suffit : il n'y a plus de billet signe depuis la bascule.
 *
 * Le serveur n'affiche ce contenu que s'il est charge dans un cadre ; toute
 * navigation directe vers cette adresse declenche un telechargement.
 */
export function reportContentUrl(diagnosticId: string): string {
  return `/contenu-rapport/${diagnosticId}`;
}
